import { createActionGroup, emptyProps, props } from '@ngrx/store';

import { Delta } from 'quill/core';

import { ActionSource } from 'src/app/state/action-sources';

import {
    BlueSageCardOnFileFragment,
    CreateTransactionMutation,
    FullTransactionFragment,
} from 'src/generated/graphql.generated';

import {
    PaymentTypeConfig,
    CreateTransactionMethod,
    CreateTransactionStep,
    CreateTransactionTarget,
    BlueSageTerminalIntent,
} from './create-transaction-dialog.state';

export const CreateTransactionDialogActions = createActionGroup({
    source: ActionSource.CREATE_TRANSACTION_DIALOG,
    events: {
        // Requirement: the holds and invoices are read live where they are needed, never
        // carried in this payload — a snapshot once released a hold that had already
        // been completed elsewhere.
        'Create Transaction Button Clicked': props<{
            jobId: string;
            customerId: string;
            currency: string;
        }>(),
        'Work Order Create Transaction Button Clicked': props<{
            jobId: string;
            customerId: string;
            currency: string;
            invoiceId: string | undefined;
            amountMinor: number;
        }>(),

        'Dispatch Card Create Transaction Button Clicked': props<{
            jobId: string;
            customerId: string;
            currency: string;
        }>(),

        'Invoice Pay Button Clicked': props<{
            jobId: string;
            customerId: string;
            currency: string;
            invoiceId: string;
            amountMinor: number | undefined;
        }>(),

        'Booking Pay Button Clicked': props<{
            jobId: string;
            customerId: string;
            currency: string;
            amountMinor: number | undefined;
        }>(),

        'Job Load Error': props<{ jobId: string; error: Error }>(),

        'Open Holds Found On Job': emptyProps(),

        'Hold Tile Clicked': props<{ transactionId: string; amountMinor: number }>(),

        'Create New Transaction Button Clicked': emptyProps(),

        'Target Tile Clicked': props<{ target: CreateTransactionTarget }>(),

        'New Invoice Tile Clicked': props<{ eventIds: string[]; askForEvents: boolean }>(),
        'Invoice Event Tile Clicked': props<{ eventId: string; singleSelect: boolean }>(),
        'Continue To Amount Button Clicked': emptyProps(),

        'Target Inferred From Job': props<{ target: CreateTransactionTarget }>(),
        'Deposit Preselected': emptyProps(),
        'New Invoice Preselected': emptyProps(),

        'Amount Seeded From Targets': props<{ amountMinor: number }>(),

        'Preselected Target Unavailable': emptyProps(),

        'Amount Changed': props<{ amountMinor: number }>(),
        'Amount Chip Clicked': props<{ amountMinor: number }>(),
        'Continue To Method Button Clicked': emptyProps(),

        'Method Tile Clicked': props<{ method: CreateTransactionMethod }>(),
        'Payment Type Tile Clicked': props<{ paymentTypeId: string }>(),

        'Collecting Value Clicked': props<{ step: CreateTransactionStep }>(),

        'Back Button Clicked': emptyProps(),

        'Remarks Changed': props<{ remarks: Delta }>(),

        'Send Receipt Toggled': props<{ sendReceipt: boolean }>(),

        'Load Payment Types Success': props<{ paymentTypes: PaymentTypeConfig[] }>(),
        'Load Payment Types Error': props<{ error: Error }>(),

        'Load Payment Type Success': props<{
            paymentTypeId: string | undefined;
            differentialPct: number | null;
            differentialPctKnown: boolean;
        }>(),
        'Load Payment Type Error': props<{ error: Error }>(),

        'Key In Card Button Clicked': emptyProps(),
        'Card Form Opened': props<{ embedUrl: string | undefined }>(),
        'Card Form Error': props<{ error: Error }>(),
        'Card Form Closed': emptyProps(),

        // Requirement: no PAN reaches this app — the BlueSage-hosted iframe posts back
        // only a one-time token.
        'Card Token Received': props<{ cardToken: string; last4: string | null }>(),
        'Save Card Toggled': props<{ saveCard: boolean }>(),

        'Cards On File Loaded': props<{ cardsOnFile: BlueSageCardOnFileFragment[] }>(),
        'Cards On File Error': props<{ error: Error }>(),
        'Card On File Selected': props<{ token: string }>(),
        'Delete Card On File Clicked': props<{ token: string }>(),

        'New Card Selected': emptyProps(),
        // Requirement: this discards an unstored token, not a saved card — the one-time
        // token simply expires unused at BlueSage.
        'Discard New Card Clicked': emptyProps(),
        'Delete Card On File Success': props<{ token: string }>(),
        'Delete Card On File Error': props<{ error: Error }>(),

        'Charge Card Button Clicked': emptyProps(),
        // Records the row a charge attempt created: a declined card never reaches
        // `createTransactionSuccess`, so nothing else would know which row to take back.
        'Charge Transaction Created': props<{
            transactionId: string;
            data: CreateTransactionMutation;
        }>(),
        'Charge Card Success': props<{ data: CreateTransactionMutation }>(),
        'Charge Card Error': props<{ error: Error }>(),

        'Send To Terminal Button Clicked': props<{ intent: BlueSageTerminalIntent }>(),

        'Send Pay Link Button Clicked': emptyProps(),
        'Pay Link Created': props<{
            transactionId: string;
            payUrl: string | undefined;
            data: CreateTransactionMutation;
        }>(),

        'Send New Pay Link Button Clicked': emptyProps(),
        'Pay Link Resent': props<{
            transactionId: string;
            payUrl: string | undefined;
            created: FullTransactionFragment;
            cancelled: FullTransactionFragment;
        }>(),
        'Vault On Pay Link Toggled': props<{ vaultOnPayLink: boolean }>(),
        'Pay Link Copy Button Clicked': props<{ payUrl: string }>(),

        'Record Payment Button Clicked': emptyProps(),
        'Record Payment Success': props<{ data: CreateTransactionMutation }>(),

        'Send Square Checkout Button Clicked': emptyProps(),
        'Square Checkout Link Sent': props<{
            transactionId: string;
            data: CreateTransactionMutation;
        }>(),

        'Create Transaction Success': props<{
            transactionId: string;
            authorize: boolean;
            amountMinor: number;
            epi: string | undefined;
            data: CreateTransactionMutation;
        }>(),
        'Create Transaction Error': props<{ error: Error }>(),

        // Carries its own row and job id because closing the dialog already reset the
        // state that named them.
        'Abandoned Transaction Created': props<{
            transactionId: string;
            jobId: string;
            data: CreateTransactionMutation;
        }>(),

        // Requirement: Complete is a two-step. A hold tile opens the confirmation naming
        // the terminal, the amount and the seconds the customer has at the tip prompt;
        // only the press on that screen moves money.
        'Complete Authorization From Row Clicked': props<{
            jobId: string;
            customerId: string;
            currency: string;
            openPreauthTransactionId: string;
            amountMinor: number;
        }>(),

        'Complete Amount Seeded From Balance': props<{ amountMinor: number }>(),
        'Complete Amount Changed': props<{ amountMinor: number }>(),
        'Complete Cancelled': emptyProps(),

        'Unsent Transaction Cancelled': props<{ transaction: FullTransactionFragment }>(),
        'Unsent Transaction Cancel Error': props<{ error: Error }>(),

        'Done Button Clicked': emptyProps(),
        'Dialog Closed': emptyProps(),
    },
});
